import { observer } from "mobx-react";
import React from "react";
import {
  Card,
  Stack,
  Group,
  Text,
  ActionIcon,
  CopyButton,
  Center,
  Image,
  Box,
} from "@mantine/core";
import {
  IconBolt,
  IconCopy,
  IconCheck,
  IconEdit,
  IconClock,
  IconHourglass,
} from "@tabler/icons-react";
import { BobsWorkspace } from "../../stores/BobsWorkspace";
import { BOB_MODALS, BOB_PAGES } from "../../helpers/constants";
import { holdAmountSatoshis } from "../../helpers/holdInvoices";
import {
  getExchangeRateParts,
  pluralize,
} from "../../helpers/formatNumbers";

const glass = {
  background: "rgba(20,20,20,.45)",
  border: "1px solid rgba(255,255,255,.05)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: 18,
} as const;

const BobHome = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const bob = bobsWorkspace.selectedBob;
    const { btcFirst, fiatFirst } = getExchangeRateParts({
      currency: bob.currencyCode,
      currencySnapshot: bobsWorkspace.exchangeRates.data,
    });
    // console.log("bob.isLikelyDefaultAddress", bob.isLikelyDefaultAddress);

    return (
      <Stack gap={32} px="md" pt="md">
        {/* balance */}
        <Card p="lg" shadow="md" style={glass}>
          <Stack gap={6} align="center">
            <Group gap={6}>
              {/* <IconBolt size={44} color="#00BFFF" stroke={1.3} /> */}
              <IconBolt
                size={36}
                stroke={1.2}
                color="var(--mantine-color-blue-light-color)" // ← new colour
              />
              <Text
                ml={"-0.25rem"}
                fw={800}
                fz={32}
                style={{ letterSpacing: -0.5 }}
              >
                {bob.safeBalanceInSatoshis.toLocaleString()}
              </Text>
              <Text c="dimmed" fz="lg">
                {pluralize(bob.safeBalanceInSatoshis, "sat")}
              </Text>
            </Group>

            <Text c="gray.4">{bob.formatFiat(bob.safeBalanceInSatoshis)}</Text>

            {bob.inFlightSatoshis > 0 && (
              <Group
                mt={4}
                px={10}
                py={4}
                gap={6}
                style={{
                  background: "rgba(255, 196, 0, 0.15)",
                  border: "1px solid rgba(255, 196, 0, 0.35)",
                  borderRadius: 12,
                }}
              >
                <IconBolt size={18} color="#FFC400" stroke={1.7} />
                <Text
                  fw={700}
                  fz="sm"
                  c="yellow.3"
                  style={{ letterSpacing: -0.3 }}
                >
                  {bob.inFlightSatoshis.toLocaleString()}{" "}
                  in-flight&nbsp;{pluralize(bob.inFlightSatoshis, "sat")}
                </Text>
              </Group>
            )}

            {/* hold payments waiting for a decision: never part of the balance */}
            {bob.undecidedHoldInvoices.length > 0 && (
              <Group
                mt={4}
                px={10}
                py={4}
                gap={6}
                onClick={() => bob.setOpenPage(BOB_PAGES.HOLD_INVOICES)}
                style={{
                  background: "rgba(250, 82, 82, 0.15)",
                  border: "1px solid rgba(250, 82, 82, 0.35)",
                  borderRadius: 12,
                  cursor: "pointer",
                }}
              >
                <IconHourglass size={18} color="#FA5252" stroke={1.7} />
                <Text
                  fw={700}
                  fz="sm"
                  c="red.3"
                  style={{ letterSpacing: -0.3 }}
                >
                  Held:{" "}
                  {bob.undecidedHoldInvoices
                    .reduce((sum, hold) => sum + holdAmountSatoshis(hold), 0)
                    .toLocaleString()}{" "}
                  sats awaiting decision
                </Text>
              </Group>
            )}

            {bob.pendingOnchainBalanceInSatoshis > 0 && (
              <Group
                mt={4}
                px={10}
                py={4}
                gap={6}
                style={{
                  background: "rgba(255, 165, 0, 0.15)",
                  border: "1px solid rgba(255, 165, 0, 0.35)",
                  borderRadius: 12,
                }}
              >
                <IconClock size={18} color="#FFA500" stroke={1.7} />
                <Text
                  fw={700}
                  fz="sm"
                  c="orange.3"
                  style={{ letterSpacing: -0.3 }}
                >
                  {bob.pendingOnchainBalanceInSatoshis.toLocaleString()} pending
                </Text>
              </Group>
            )}
          </Stack>
        </Card>

        {/* lightning address */}
        <Card p="lg" shadow="md" style={glass}>
          <Stack gap={8}>
            <Text fz="xs" fw={600} c="gray.4" tt="uppercase">
              lightning address
            </Text>

            <Group gap={6} wrap="nowrap">
              <Text
                fz="sm"
                ff="monospace"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {bob.lightningAddress}
              </Text>

              <CopyButton value={bob.lightningAddress}>
                {({ copied, copy }) => (
                  <ActionIcon
                    size="md"
                    variant={copied ? "filled" : "outline"}
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                    aria-label="copy address"
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                )}
              </CopyButton>

              {bob.isLikelyDefaultAddress && (
                <ActionIcon
                  size="md"
                  variant="subtle"
                  aria-label="edit address"
                  onClick={() => bob.setOpenModal(BOB_MODALS.EDIT_ALIAS)}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              )}
            </Group>
          </Stack>
        </Card>

        {/* rates */}
        <Group grow>
          <Card
            p="md"
            shadow="sm"
            style={{ ...glass, backdropFilter: "blur(4px)" }}
          >
            <Center>
              <Text fw={700} c="var(--mantine-color-orange-light-color)">
                {btcFirst}
              </Text>
            </Center>
          </Card>

          <Card
            p="md"
            shadow="sm"
            style={{ ...glass, backdropFilter: "blur(4px)" }}
          >
            <Center>
              <Text fw={700} c="var(--mantine-color-blue-light-color)">
                {fiatFirst}
              </Text>
            </Center>
          </Card>
        </Group>

        {/* brand */}

        <Box style={{ filter: "drop-shadow(0 0 6px rgba(0,0,0,.6))" }}>
          <Image
            style={{
              opacity: 0.42,
              mixBlendMode: "color-dodge",
              filter: "brightness(1.8)",
            }}
            src="https://daxwktpz4hkwd.cloudfront.net/logos/rizful-logo-mobile.png"
            h={64}
            fit="contain"
            alt="Rizful"
          />
        </Box>
      </Stack>
    );
  }
);

export default BobHome;
